import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { GALLERY_IMAGES } from "../data/menu";
import { Flourish, PetalField, Blossom } from "../components/decor";
import { Reveal } from "../components/Reveal";

export default function Gallery() {
  const [open, setOpen] = useState(null);

  const step = useCallback(
    (dir) => setOpen((cur) => (cur + dir + GALLERY_IMAGES.length) % GALLERY_IMAGES.length),
    []
  );

  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, step]);

  return (
    <main data-testid="gallery-page">
      <section className="relative overflow-hidden px-5 pb-14 pt-20 text-center md:pt-28">
        <PetalField count={8} />
        <Reveal>
          <p className="text-xs font-medium uppercase tracking-[0.3em] text-[#C9A227]">Moments</p>
          <h1 className="mt-3 font-serif text-5xl font-black text-[#9B2C2C] md:text-6xl">Inside the Bloom</h1>
          <div className="mt-5 flex justify-center"><Flourish className="h-4 w-56" /></div>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-[#B86B77]">
            Pink walls, hanging blossoms, golden light — a little tour of our two-storey home in Dumka.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24 md:px-10">
        <div className="columns-1 gap-5 sm:columns-2 lg:columns-3 [column-fill:_balance]">
          {GALLERY_IMAGES.map((img, i) => (
            <motion.button
              key={img.src}
              data-testid={`gallery-item-${i}`}
              onClick={() => setOpen(i)}
              className="group relative mb-5 block w-full overflow-hidden rounded-[12px] shadow-[0_8px_30px_rgba(155,44,44,0.10)]"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
              />
              <span className="absolute inset-0 bg-[#9B2C2C]/0 transition-colors duration-500 group-hover:bg-[#9B2C2C]/15" />
              <Blossom className="absolute right-3 top-3 h-6 w-6 text-white opacity-0 transition-opacity duration-500 group-hover:opacity-90" />
            </motion.button>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {open !== null && (
          <motion.div
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[70] flex items-center justify-center bg-[#F8E8E8]/95 p-4 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(null)}
          >
            <button
              data-testid="lightbox-close"
              onClick={() => setOpen(null)}
              className="absolute right-5 top-5 rounded-full bg-[#9B2C2C] p-3 text-white transition-transform hover:scale-110"
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-3 text-[#9B2C2C] shadow-md transition-transform hover:scale-110 md:left-8"
              aria-label="Previous"
            >
              <ChevronLeft size={22} />
            </button>
            <motion.figure
              key={open}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-[82vh] max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={GALLERY_IMAGES[open].src}
                alt={GALLERY_IMAGES[open].alt}
                className="max-h-[74vh] w-auto max-w-full rounded-[12px] border-4 border-white object-contain shadow-[0_30px_80px_rgba(155,44,44,0.25)]"
              />
              <figcaption className="mt-4 text-center font-serif text-base italic text-[#9B2C2C]">
                {GALLERY_IMAGES[open].alt}
              </figcaption>
            </motion.figure>
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-3 text-[#9B2C2C] shadow-md transition-transform hover:scale-110 md:right-8"
              aria-label="Next"
            >
              <ChevronRight size={22} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
